import React, { useEffect, useState, useRef } from 'react';
import { Check, X, Sparkles } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from '../../lib/adminApi';
import { ADMIN_APPROVALS } from '../../constants/testIds';

const AdminApprovals = () => {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('gbp_post');
  const [clientId, setClientId] = useState('');
  const [context, setContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [reviewingId, setReviewingId] = useState(null);
  const isReviewingRef = useRef(false);

  const load = () => {
    adminApi
      .get('/admin/approvals', { params: { status: 'pending' } })
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => {
    adminApi.get('/admin/clients').then(({ data }) => setClients(data));
  }, []);

  const review = async (id, status) => {
    if (isReviewingRef.current) return;
    isReviewingRef.current = true;
    setReviewingId(id);
    try {
      const { data } = await adminApi.patch(`/admin/approvals/${id}`, { status });
      if (data.publish_note) {
        window.alert(data.publish_note);
      }
      load();
    } catch (err) {
      window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      isReviewingRef.current = false;
      setReviewingId(null);
    }
  };

  const generate = async () => {
    if (!context || !clientId) return;
    setGenError('');
    setGenerating(true);
    try {
      await adminApi.post('/admin/approvals/generate', { type, context, rating: 5, client_id: clientId });
      setContext('');
      load();
    } catch (err) {
      setGenError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid={ADMIN_APPROVALS.page}>
      <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Approval queue</h1>
      <p className="text-stone-500 text-sm mb-7">Draft with AI for a client — they'll review and approve it themselves in their portal.</p>

      <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-8">
        <h2 className="text-sm font-bold text-stone-800 mb-3 flex items-center gap-2">
          <Sparkles size={15} className="text-[#F97316]" /> Generate a new AI draft
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            data-testid={ADMIN_APPROVALS.clientSelect}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="px-3 py-2.5 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
          >
            <option value="">Select client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.business_name}</option>
            ))}
          </select>
          <select
            data-testid={ADMIN_APPROVALS.typeSelect}
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-3 py-2.5 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
          >
            <option value="gbp_post">Google Business Post</option>
            <option value="review_reply">Review Reply</option>
          </select>
          <input
            data-testid={ADMIN_APPROVALS.contextInput}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={type === 'gbp_post' ? 'Business context, e.g. 15 years in business, 10% off this month' : 'Paste the customer review text'}
            className="flex-1 px-3.5 py-2.5 rounded-lg bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
          />
          <button
            data-testid={ADMIN_APPROVALS.generateButton}
            onClick={generate}
            disabled={generating || !context || !clientId}
            className="px-5 py-2.5 rounded-lg bg-[#4F46E5] text-white text-sm font-bold hover:bg-[#4338CA] transition disabled:opacity-60 whitespace-nowrap"
          >
            {generating ? 'Drafting…' : 'Generate draft'}
          </button>
        </div>
        {genError && <p className="text-xs text-red-600 mt-2">{genError}</p>}
      </div>

      {!loading && items.length === 0 ? (
        <div data-testid={ADMIN_APPROVALS.emptyState} className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center">
          <p className="text-stone-500 text-sm">Nothing to review yet. Generate a draft above to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl border border-stone-200 p-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase mb-1">
                  {item.type.replace('_', ' ')} {item.client_name && <span className="text-[#4F46E5]">• {item.client_name}</span>}
                </p>
                <p className="text-sm text-stone-800">{item.content}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid={ADMIN_APPROVALS.approveButton} disabled={reviewingId === item.id} onClick={() => review(item.id, 'approved')} className="p-2 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  <Check size={16} />
                </button>
                <button data-testid={ADMIN_APPROVALS.rejectButton} disabled={reviewingId === item.id} onClick={() => review(item.id, 'rejected')} className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminApprovals;
